// USP Review Page - VISUALLY UPDATED with InsureVision Colors
// Dark purple hero, magenta accents, white CTAs
import {useState} from 'react'
import {Link} from 'react-router-dom'
import {useAuth} from '../../context/AuthContext'

// USP data
const USP_DATA = [{
  id: 'underwriter-ready',
  title: 'Lower Insurance Premiums',
  summary: 'Data-driven safety records that build underwriting confidence.',
  detail: (<>
      Prove your fleet's actual risk profule to insurers.
      <br/>
      Fleets using video-based risk data have negotiated 10-20% premium reductions.
    </>),
  icon: (<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>)
}, {
  id: 'nuclear-claims',
  title: 'Stop Nuclear Claims',
  summary: 'Prevent multi-million dollar / pound / euro catastrophic loss events.',
  detail: 'Plaintiff attorneys are pulling dashcam footage to prove negligence. Fleetvision ensures you see the patterns BEFORE they become evidence against you.',
  icon: (<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
    </svg>)
}, {
  id: 'ai-insights',
  title: 'AI-Powered Risk Detection and Targeted Driver Coaching',
  summary: 'Identify and address claim risks before they become catastrophic incidents.',
  detail: 'Stop reviewing 300 clips a day. Our AI identifies the 3% of drivers who represent 30% of your risk. Coach the right people, not everyone.',
  icon: (<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
    </svg>)
}, {
  id: 'seamless-integration',
  title: 'Seamless Integration',
  summary: 'No new hardware. Better insights from footage you already have.',
  detail: 'FleetVision reprocesses your existing footage with vision transformer AI - the same technology that powers self-driving cars. Instant connection via a simple API integration.',
  icon: (<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
    </svg>)
}]

const USPCard = ({usp, isExpanded, onToggle}) => {
  return (<div
      className={`
        bg-white rounded-xl transition-all duration-300 cursor-pointer overflow-hidden
        ${isExpanded ? 'ring-2 ring-accent-600 shadow-strong' : 'shadow-soft hover:shadow-medium border border-slate-200 hover:border-accent-200'}
      `}
      onClick={onToggle}
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          {/* Icon - uses brand purple */}
          <div className={`
            flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-colors duration-300
            ${isExpanded ? 'bg-accent-100 text-accent-600' : 'bg-brand-100 text-brand-600'}
          `}>
            {usp.icon}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-brand-950">{usp.title}</h3>
              <svg
                className={`w-5 h-5 text-slate-400 transition-transform duration-300 flex-shrink-0 ml-2 ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
              </svg>
            </div>
            <p className="mt-1 text-slate-600">{usp.summary}</p>

            {isExpanded && (<div className="mt-4 pt-4 border-t border-slate-100 animate-fade-in">
                <p className="text-slate-700 leading-relaxed">{usp.detail}</p>
              </div>)}
          </div>
        </div>
      </div>
    </div>)
}

const USPReviewPage = () => {
  const [expandedId, setExpandedId] = useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const {isAuthenticated} = useAuth()

  const toggleExpanded = (id) => {
    setExpandedId(expandedId === id ? null : id)
  }

  const videoUrl = import.meta.env.VITE_USP_VIDEO_URL
  const videoThumbnailUrl = import.meta.env.VITE_USP_VIDEO_THUMBNAIL_URL

  return (<div className="min-h-screen">
      <header className="w-full flex justify-center">
        <div
          className="bg-[rgb(32_26_70_/_27%)] backdrop-blur-[25px] rounded-2xl py-3 px-6 fixed z-[999] w-[90%] top-5 xl:w-[80%] lg:w-[85%]">
          <div className="flex items-center justify-between h-[50px] gap-0">
            <a href="https://insurevision.ai" className="flex items-center">
              <img src="/assets/logo.png" alt="InsureVision Logo" className="h-[71px] w-auto block"/>
            </a>

            <nav className="hidden lg:block ml-auto lg:mr-8">
              <ul className="flex gap-8 m-0">
                <li className="relative group">
                  <a href="https://insurevision.ai/about/"
                     className="flex items-center gap-1 py-3.5 text-white font-normal text-base hover:text-[#a78bfa] transition-colors duration-200">
                    About
                  </a>
                </li>
                <li className="relative group">
                  <a href="https://fleetvision-onboarding.insurevision.ai/"
                     className="flex items-center gap-1 py-3.5 text-white font-normal text-base hover:text-[#a78bfa] transition-colors duration-200">
                    FleetVision
                  </a>
                </li>
                <li className="relative group">
                  <a href="mailto:sales@insurevision.ai"
                     className="flex items-center gap-1 py-3.5 text-white font-normal text-base hover:text-[#a78bfa] transition-colors duration-200">
                    Get in touch
                  </a>
                </li>
                {isAuthenticated ? <li className="relative group">
                  <Link to="/onboarding"
                     className="flex items-center gap-1 py-3.5 text-white font-normal text-base hover:text-[#a78bfa] transition-colors duration-200">Continue
                    Your Demo Journey →</Link>
                </li> : <>
                  <li className="relative group">
                    <Link to="/login"
                       className="flex items-center gap-1 py-3.5 text-white font-normal text-base hover:text-[#a78bfa] transition-colors duration-200">Log
                      in</Link>
                  </li>
                  <li className="relative group">
                    <Link to="/signup"
                       className="flex items-center gap-1 rounded-xl py-3.5 px-8 bg-white text-[#a78bfa] font-normal text-base hover:text-[#a78bfa] transition-colors duration-200">Get
                      started</Link>
                  </li>
                </>}
              </ul>
            </nav>

            <button
              className="block w-6 h-6 relative lg:hidden mobile-menu-toggle"
              aria-label="Toggle menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span
                className={`block w-6 h-0.5 bg-white absolute top-1/2 -translate-y-1/2 transition-all duration-300 ${mobileMenuOpen ? 'rotate-45 before:opacity-0 after:rotate-90 after:top-0' : ''} before:content-[''] before:block before:w-6 before:h-0.5 before:bg-white before:absolute before:-top-2 before:transition-all before:duration-300 after:content-[''] after:block after:w-6 after:h-0.5 after:bg-white after:absolute after:-bottom-2 after:transition-all after:duration-300`}></span>
            </button>
          </div>

          <div
            className={`${mobileMenuOpen ? 'block' : 'hidden'} bg-[#201a46] py-4 absolute top-full left-0 right-0 z-[99] rounded-2xl mobile-menu`}>
            <div className="w-full max-w-7xl mx-auto px-4">
              <ul className="mb-6">
                <li>
                  <a href="https://insurevision.ai/about/"
                     className="block py-3 text-white font-medium border-b border-white/10 mobile-nav-link">
                    About
                  </a>
                </li>
                <li>
                  <a href="https://fleetvision-onboarding.insurevision.ai/"
                     className="block py-3 text-white font-medium border-b border-white/10 mobile-nav-link">
                    FleetVision
                  </a>
                </li>
                <li>
                  <a href="mailto:sales@insurevision.ai"
                     className="block py-3 text-white font-medium border-b border-white/10 mobile-nav-link">
                    Get in touch
                  </a>
                </li>
                {isAuthenticated ? (<li>
                    <Link to="/onboarding"
                       className="block py-3 text-white font-medium border-b border-white/10 mobile-nav-link">
                      Continue Your Demo Journey →
                    </Link>
                  </li>) : (<>
                    <li>
                      <Link to="/login"
                         className="block py-3 text-white font-medium border-b border-white/10 mobile-nav-link">
                        Log in
                      </Link>
                    </li>
                    <li>
                      <Link to="/signup"
                         className="block py-3 mt-4 text-center rounded-xl bg-white text-[#a78bfa] font-medium mobile-nav-link">
                        Get started
                      </Link>
                    </li>
                  </>)}
              </ul>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Dark purple gradient */}
      <section className="hero-gradient pt-40 pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          {/* Main headline - white, all caps */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-white uppercase tracking-tight">
            MORE CONTEXT. LESS RISK
          </h1>

          {/* Accent subheadline - magenta */}
          <p className="mt-6 text-2xl sm:text-3xl font-bold text-accent-600 uppercase">
            Lower Insurance. Stop Nuclear Claims. Save Lives.
          </p>

          {/* Body text - white with transparency */}
          <p className="mt-6 text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
            FleetVision analyzes your dashcam footage using InsureVision's
            context-based AI to identify risk patterns and prevent catastrophic claims
            before they happen.
          </p>

        </div>
      </section>

      {/* Video section - Light background */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="aspect-video bg-brand-950 rounded-2xl overflow-hidden shadow-strong">
            {videoUrl ? (<video
                className="w-full h-full object-cover"
                controls
                preload="metadata"
                poster={videoThumbnailUrl}
              >
                <source src={videoUrl} type="video/mp4"/>
                Your browser does not support the video tag.
              </video>) : (<div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                  <p className="text-white/60">Demo video</p>
                  <p className="text-sm text-white/40 mt-1">2 min overview of FleetVision in action</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* USP Cards - Light background */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-display font-bold text-brand-950 mb-8 text-center">
            Why FleetVision?
          </h2>
          <div className="space-y-4">
            {USP_DATA.map((usp) => (<USPCard
                key={usp.id}
                usp={usp}
                isExpanded={expandedId === usp.id}
                onToggle={() => toggleExpanded(usp.id)}
              />))}
          </div>
        </div>
      </section>

      {/* CTA Section - Magenta gradient */}
      <section className="accent-gradient py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
            See This With Your Own Data
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Experience FleetVision using footage from your own fleet.
            We'll analyze your risk patterns and show you exactly what claim indicators we find.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-white text-accent-700 font-bold rounded-xl hover:bg-gray-50 transition-all shadow-strong hover:shadow-xl text-lg"
          >
            Get Your Personalized Demo
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </Link>
          <p className="mt-4 text-white/80 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold underline hover:text-white">
              Log in here
            </Link>
          </p>
        </div>
      </section>

      {/* Footer - Dark purple */}
      <footer className="bg-black border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-white/60">
              © {new Date().getFullYear()} Advanced Automobile Solutions Ltd.
            </p>
            <div className="flex gap-6">
              <a href="https://insurevision.ai/legal/privacy-policy/" target="_blank" rel="noopener noreferrer"
                 className="text-sm text-white/60 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="https://insurevision.ai/legal/cookie-policy/" target="_blank" rel="noopener noreferrer"
                 className="text-sm text-white/60 hover:text-white transition-colors">
                Cookie Policy
              </a>
              <a href="mailto:sales@insurevision.ai"
                 className="text-sm text-white/60 hover:text-white transition-colors">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>)
}

export default USPReviewPage
